import React, { useState } from 'react';
import { FaStar } from 'react-icons/fa';
import { X } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

export default function ReviewModal({ isOpen, onClose, onSubmit, orderTitle }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [deliverySpeed, setDeliverySpeed] = useState('fast');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const setAlert = useAlert();

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setAlert({ type: 'error', message: 'Please select a star rating' });
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({ rating, comment, deliverySpeed });
      onClose();
    } catch (error) {
      console.error('Review submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300 font-['Poppins']">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-[#174568]">Leave a Review</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">How was your project?</p>
            <h3 className="text-lg font-medium text-[#174568] line-clamp-1">{orderTitle}</h3>
          </div>

          <div className="flex flex-col items-center gap-4">
            <div className="flex gap-2">
              {[...Array(5)].map((_, index) => {
                const starValue = index + 1;
                return (
                  <button
                    key={index}
                    type="button"
                    className="transition-transform duration-200 hover:scale-125 focus:outline-none"
                    onClick={() => setRating(starValue)}
                    onMouseEnter={() => setHover(starValue)}
                    onMouseLeave={() => setHover(0)}
                  >
                    <FaStar
                      size={40}
                      color={(hover || rating) >= starValue ? "#ffc107" : "#e4e5e9"}
                      className="transition-colors duration-200"
                    />
                  </button>
                );
              })}
            </div>
            {rating > 0 && (
              <p className="text-sm font-semibold text-[#174568]">
                {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Share your experience</label>
            <textarea
              className="w-full h-32 p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#174568] focus:border-transparent outline-none transition-all resize-none text-sm"
              placeholder="What was it like working with the team?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              required
            ></textarea>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">How was the delivery speed?</label>
            <div className="flex gap-2">
              {['fast', 'extra-fast', 'express'].map((speed) => (
                <button
                  key={speed}
                  type="button"
                  onClick={() => setDeliverySpeed(speed)}
                  className={`flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-all ${
                    deliverySpeed === speed
                      ? 'bg-[#174568] text-white border-[#174568]'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {speed.charAt(0).toUpperCase() + speed.slice(1).replace('-', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || rating === 0}
              className="flex-1 px-4 py-3 rounded-xl bg-[#174568] text-sm font-medium text-white hover:bg-[#0f2e46] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-900/10"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
